// /compare/studio-ninja: Pixally vs Studio Ninja.
//
// Read the header of honeybook.js first: every rule there applies here. Studio
// Ninja's facts, with sources and exact words:
// docs/geo/compare-honeybook-alternatives-evidence.md. Studio Ninja does not
// publish its payment-processing fees on its pricing page, so this page makes no
// fee comparison and does not use the 'fees' drawing.

export const ROUTE = '/compare/studio-ninja';
export const UPDATED_AT = '2026-10-05';
export const CHECKED_AT = 'October 1, 2026';
export const CHECKED_LABEL = 'Studio Ninja facts checked';
export const HERO_ART = 'versus';

export const TITLE = 'Pixally vs Studio Ninja';
export const SEO_TITLE = 'Pixally vs Studio Ninja for Wedding Photographers and Videographers';
export const SEO_DESCRIPTION =
  'How Pixally and Studio Ninja compare on price, galleries, film delivery, team members, second shooters and post-production, with every Studio Ninja fact sourced.';

export const LEDE =
  'Studio Ninja is a low-cost CRM for photographers that connects to gallery platforms such as Pic-Time and ShootProof rather than hosting galleries itself. Pixally is a CRM built for wedding photographers and videographers, launching on January 1, 2027, with galleries, film delivery, second shooters and post-production tracking built in.';

const NINJA_PRICING = 'https://www.studioninja.co/pricing/';

export const BODY = [
  {
    type: 'paragraph',
    text: 'If you want an inexpensive CRM today and already deliver through Pic-Time or ShootProof, Studio Ninja is hard to beat on price. If you want delivery, your crew and your edit in the same system as your bookings, the differences below matter.',
  },
  { type: 'heading', level: 2, text: 'Side by side' },
  {
    type: 'paragraph',
    text: `The Studio Ninja column comes from Studio Ninja's own pricing page, checked ${CHECKED_AT}.`,
  },
  {
    type: 'table',
    label: 'Pixally and Studio Ninja compared',
    head: ['', 'Pixally', 'Studio Ninja'],
    rows: [
      ['Available', 'From January 1, 2027', 'Now'],
      ['Plans, billed monthly', 'Free $0, Basic $39, Pro $69, Studio $119', 'Starter $16, Pro $27, Master $40'],
      ['Plans, billed yearly', 'Basic $33, Pro $58, Studio $99 a month', 'Starter $160, Pro $270, Master $400 a year'],
      ['Free plan or trial', 'A 14-day free trial with no card, then the Free plan: up to 5 active booked projects', 'No free plan. A 7-day trial'],
      ['Entry plan limit', 'Free: 5 active booked projects; Basic and up: unlimited', 'Starter: 5 active jobs; Pro and up: unlimited'],
      ['Client galleries', 'Photo, video and hybrid galleries built in, on paid plans', 'Not built in: connects to Pic-Time and ShootProof'],
      ['Film delivery', 'Built in: 50 GB to start on paid plans, then packs; no limit on film length', 'Not built in'],
      ['Workflow automations', 'On Basic and up', 'On Pro and Master'],
      ['Team members', '1, 1, 2, unlimited; more for $9 a month each', '1, 3, unlimited'],
      ['Second shooters and contractors', 'Free contractor accounts; second shooter management and a contractor portal on Pro and Studio', "Not described on Studio Ninja's pricing page"],
      ['Post-production tracking', 'On Pro and Studio', "Not described on Studio Ninja's pricing page"],
      ['More than one brand', '1, 1, 2, unlimited brands', '1, 1, 3 brands'],
      ['Accounting', 'QuickBooks Online on Pro and Studio', 'Xero and QuickBooks on every plan'],
      ['Moving your data in', 'Free migration of your last 12 months of active projects', 'Free migration of current and future jobs'],
    ],
  },
  { type: 'heading', level: 2, text: 'When Studio Ninja is the better choice' },
  {
    type: 'list',
    items: [
      'It exists today. Pixally launches on January 1, 2027.',
      'It costs much less. Pro is $270 a year with 3 users and unlimited jobs; Pixally Pro is $690 a year billed yearly. Master, with unlimited users and 3 brands, is $400 a year.',
      'It connects to the tools many photographers already sell through: Pic-Time and ShootProof for galleries, Fundy and ProSelect for albums and in-person sales, and Xero or QuickBooks for accounting.',
      'Free one-on-one training and in-app chat support on every plan.',
    ],
  },
  { type: 'heading', level: 2, text: 'When Pixally is the better choice' },
  {
    type: 'paragraph',
    text: 'Pixally is the better choice if you want galleries and films delivered from your CRM, run second shooters and editors, or want to start on a free plan.',
  },
  { type: 'heading', level: 3, text: 'Galleries and films' },
  { type: 'art', name: 'film' },
  {
    type: 'paragraph',
    text: 'Studio Ninja connects to Pic-Time and ShootProof for delivery, which means a second subscription. Pixally delivers photos, films and hybrid galleries from the same project as the booking, with no limit on film length, 50 GB of film delivery to start on every paid plan and more in packs that never expire.',
  },
  { type: 'heading', level: 3, text: 'Second shooters and contractors' },
  { type: 'art', name: 'crew' },
  {
    type: 'paragraph',
    text: "In Pixally a second shooter does not need a team seat. They get a [free contractor account](/contractor-terms) that works across every studio they shoot for, with their availability, travel fee and tax form in one place, and another studio never sees what you pay them. Second shooter management and the contractor portal are on Pro (up to 10) and Studio (unlimited).",
  },
  { type: 'heading', level: 3, text: 'Post-production' },
  { type: 'art', name: 'pipeline' },
  {
    type: 'paragraph',
    text: "Pixally's post-production management (Pro and Studio) follows the stages of a wedding edit: uploads, culling and selects, edits assigned to your team or outside editors with due dates, review, client revisions and delivery.",
  },
  { type: 'heading', level: 3, text: 'A free plan' },
  {
    type: 'paragraph',
    text: "Pixally starts with a 14-day free trial, no card needed, and its Free plan then covers up to 5 active booked projects with proposals, contracts and payments. Studio Ninja has a 7-day trial and then a paid plan, though its Starter plan, also limited to 5 active jobs, is only $160 a year.",
  },
  { type: 'heading', level: 2, text: 'Switching from Studio Ninja' },
  { type: 'art', name: 'switching' },
  {
    type: 'paragraph',
    text: 'Pixally will move your last 12 months of active client projects across for free, and turn your existing documents into Pixally templates. Winter, when the season is quiet, is the easiest time to switch.',
  },
];

export const FAQ = [
  {
    q: 'Does Studio Ninja have client galleries?',
    a: 'Not built in. Studio Ninja connects to Pic-Time and ShootProof for galleries. Pixally has photo, video and hybrid galleries built in on paid plans.',
  },
  {
    q: 'Is Studio Ninja cheaper than Pixally?',
    a: 'Yes, on the subscription. Studio Ninja is $160, $270 or $400 a year; Pixally is $390, $690 or $1,190 a year billed yearly, and also has a free plan. Pixally includes galleries and film delivery, which with Studio Ninja come from a separate subscription.',
  },
  {
    q: 'Can videographers use Studio Ninja?',
    a: 'Studio Ninja says its community includes photographers and videographers. It does not deliver films itself, so videographers use it alongside a delivery platform.',
  },
  {
    q: 'Can I move from Studio Ninja to Pixally?',
    a: 'Yes. Pixally moves your last 12 months of active projects across for free, on every plan.',
  },
  {
    q: 'Is Pixally available yet?',
    a: 'No. Pixally launches on January 1, 2027. You can join the waitlist at pixally.com.',
  },
];

export const SOURCES = [
  { label: 'Studio Ninja pricing', url: NINJA_PRICING },
  { label: 'Pixally plans: the Pixally pricing table', url: null },
  { label: 'Pixally contractor accounts: Contractor Terms of Use, Section 5.1', url: '/contractor-terms' },
];
